import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useEffect } from 'react';
import { FormProvider, useWatch } from 'react-hook-form';
import {
  CreatableSelect,
  DatePicker,
  FormLayout,
  FormRow as Row,
  FormSection as Section,
  TextArea,
  TextInput,
  Toggle,
} from '~/components';
import { ButtonFooter } from '~/components/buttons/ButtonFooter';
import { useCustomForm } from '~/hooks/useCustomForm';
import { useServerWarnings } from '~/hooks/useServerWarnings';
import { POLICY_OPTIONS } from './constants';
import {
  CreateMalpracticeInsuranceFormValues,
  MalpracticeInsuranceFormValues,
  malpracticeInsuranceSchema,
} from './types';

const TAGS = CT.malpracticeInsurance.form;

interface FormProps {
  create: boolean;
  edit: boolean;
  form: Partial<MalpracticeInsuranceFormValues>;
  addInsurance: (insuranceValues: CreateMalpracticeInsuranceFormValues) => void;
  editInsurance: (insuranceValues: MalpracticeInsuranceFormValues) => void;
  handleClose: () => void;
}
const VALID_FIELDS: (keyof MalpracticeInsuranceFormValues)[] = [
  'activeFlag',
  'effectiveDate',
  'expiryDate',
];

export const InsuranceForm = (props: FormProps) => {
  const { addInsurance, editInsurance, form, create, handleClose } = props;

  const methods = useCustomForm<MalpracticeInsuranceFormValues>({
    resolver: zodResolver(malpracticeInsuranceSchema),
    defaultValues: form,
  });
  const { handleSubmit, trigger, formState, control } = methods;

  const activeFlag = useWatch({ control, name: 'activeFlag' });
  const effectiveDate = useWatch({ control, name: 'effectiveDate' });
  const expiryDate = useWatch({ control, name: 'expiryDate' });

  useEffect(() => {
    if (formState.isSubmitted) {
      trigger(['effectiveDate', 'expiryDate']);
    }
  }, [activeFlag, effectiveDate, expiryDate, formState.isSubmitted, trigger]);

  const onSubmitSuccess = (
    values: MalpracticeInsuranceFormValues | CreateMalpracticeInsuranceFormValues
  ) => {
    if (create) {
      addInsurance(values as CreateMalpracticeInsuranceFormValues);
    } else {
      editInsurance(values as MalpracticeInsuranceFormValues);
    }
  };
  useServerWarnings(methods, form.warnings, VALID_FIELDS);

  const onSubmitError = (err: unknown) => {
    console.log(err);
  };

  return (
    <FormProvider {...methods}>
      <form data-cy={TAGS.form} onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}>
        <FormLayout>
          <Section componentsPerLine={1}>
            <Toggle
              name={'activeFlag'}
              cyTag={TAGS.activeToggle}
              label="Is this an active policy?"
            />
          </Section>
          <Section componentsPerLine={1}>
            <Row>
              <TextInput
                name={'insuranceCompany'}
                cyTag={TAGS.insuranceCompanyInput}
                label={'Insurance Company*'}
              />
            </Row>
          </Section>
          <Section componentsPerLine={1}>
            <Row>
              <TextInput name={'producer'} cyTag={TAGS.producer} label={'Producer/Agent'} />
            </Row>
          </Section>
          <Section componentsPerLine={1}>
            <Row>
              <TextInput
                name={'policyNumber'}
                cyTag={TAGS.policyNumInput}
                label={'Policy Number'}
              />
            </Row>
          </Section>
          <Section componentsPerLine={1}>
            <Row>
              <CreatableSelect
                name={'policyType'}
                cyTag={TAGS.policyType}
                label={'Policy Type'}
                options={POLICY_OPTIONS}
              />
            </Row>
          </Section>
          <Section componentsPerLine={1}>
            <Row>
              <TextArea
                name={'coverageLimits'}
                cyTag={TAGS.coverageLimitsTextBox}
                label={'Coverage Limits'}
                placeholder={'Describe coverage limits'}
              />
            </Row>
          </Section>
          <Section componentsPerLine={2}>
            <Row>
              <DatePicker
                name={'effectiveDate'}
                cyTag={TAGS.effectiveDatePicker}
                label={'Start Date*'}
              />
            </Row>
            <Row>
              <DatePicker
                name={'expiryDate'}
                cyTag={TAGS.expiryDatePicker}
                label={'Expiration Date*'}
              />
            </Row>
          </Section>
          <ButtonFooter
            secondaryButton={{
              label: 'Cancel',
              cyTag: TAGS.cancelBtn,
              onClick: handleClose,
            }}
            primaryButton={{ label: 'Save', cyTag: TAGS.submitBtn, isSubmit: true }}
          />
        </FormLayout>
      </form>
    </FormProvider>
  );
};
